import { useCallback, useState } from 'react';
import {
  cognitoResendConfirmationCode,
  cognitoSignIn,
  cognitoSignUp,
} from '../../pages/login/cognito';
import { usePostAuthMutation } from '../../store/backendApi';

const COGNITO_CLIENT_ID = process.env.REACT_APP_CLIENT_ID ?? '';
const COGNITO_REGION = 'eu-central-1';

export interface AuthSubmitResult {
  success: boolean;
  needsVerification?: boolean;
  inviteOnly?: boolean;
  error?: string;
}

/**
 * Platform-agnostic hook for handling authentication submission.
 * Manages login, signup, and token exchange with the backend.
 */
export interface UseAuthSubmitResult {
  isProcessing: boolean;
  submit: (email: string, password: string, isSignup: boolean) => Promise<AuthSubmitResult>;
}

export function useAuthSubmit(): UseAuthSubmitResult {
  const [isProcessing, setIsProcessing] = useState(false);
  const [exchangeToken] = usePostAuthMutation();

  const submit = useCallback(
    async (email: string, password: string, isSignup: boolean): Promise<AuthSubmitResult> => {
      setIsProcessing(true);

      try {
        if (isSignup) {
          // Signup flow
          const isInviteOnly = localStorage.getItem('allow_register') !== 'true';

          if (isInviteOnly) {
            setIsProcessing(false);
            return { success: false, inviteOnly: true };
          }

          const signupSuccess = await cognitoSignUp(
            COGNITO_REGION,
            COGNITO_CLIENT_ID,
            email,
            password,
          );
          setIsProcessing(false);

          if (signupSuccess) {
            return { success: false, needsVerification: true };
          } else {
            return { success: false, error: 'Sign up failed' };
          }
        } else {
          // Login flow
          const result = await cognitoSignIn(COGNITO_REGION, COGNITO_CLIENT_ID, email, password);

          if (result.success) {
            if (!result.data.IdToken) {
              return { success: false, error: 'Login failed' };
            }
            // Exchange Cognito token for session cookie
            await exchangeToken({
              tokenBodyRequest: {
                idToken: result.data.IdToken,
              },
              rememberMe: true,
            });
            setIsProcessing(false);
            return { success: true };
          } else {
            setIsProcessing(false);

            // Handle user not confirmed
            if (result.error === 'UserNotConfirmedException') {
              await cognitoResendConfirmationCode(COGNITO_REGION, COGNITO_CLIENT_ID, email);
              return { success: false, needsVerification: true };
            }
            return { success: false, error: result.error || 'Login failed' };
          }
        }
      } catch (error) {
        setIsProcessing(false);
        return { success: false, error: 'An unexpected error occurred' };
      }
    },
    [exchangeToken],
  );

  return {
    isProcessing,
    submit,
  };
}
